import React, { useEffect, useMemo, useState } from 'react';
import { Cookie, Settings, ShieldCheck, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'financialmetrics_cookie_consent_v1';
const ADSENSE_CLIENT = 'ca-pub-3668804634882511';

type CookiePreferences = {
  essential: true;
  analytics: boolean;
  advertising: boolean;
  updatedAt: string;
  version: 1;
};

const defaultPreferences: CookiePreferences = {
  essential: true,
  analytics: false,
  advertising: false,
  updatedAt: '',
  version: 1,
};

const loadPreferences = () => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookiePreferences;
    if (parsed?.version !== 1) return null;
    return {
      ...defaultPreferences,
      ...parsed,
      essential: true as const,
    };
  } catch {
    return null;
  }
};

const loadAdSense = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('financialmetrics-google-adsense')) return;

  const script = document.createElement('script');
  script.id = 'financialmetrics-google-adsense';
  script.async = true;
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
  script.crossOrigin = 'anonymous';
  document.head.appendChild(script);
};

export const CookieConsent = () => {
  const [storedPreferences, setStoredPreferences] = useState<CookiePreferences | null>(() => loadPreferences());
  const [draftPreferences, setDraftPreferences] = useState<CookiePreferences>(() => loadPreferences() || defaultPreferences);
  const [customizing, setCustomizing] = useState(false);
  const [visible, setVisible] = useState(() => !loadPreferences());

  const hasChoice = !!storedPreferences;

  useEffect(() => {
    if (storedPreferences?.advertising) {
      loadAdSense();
    }
  }, [storedPreferences]);

  useEffect(() => {
    const openSettings = () => {
      const current = loadPreferences() || defaultPreferences;
      setDraftPreferences(current);
      setCustomizing(true);
      setVisible(true);
    };

    window.addEventListener('financialmetrics:open-cookie-settings', openSettings);
    return () => window.removeEventListener('financialmetrics:open-cookie-settings', openSettings);
  }, []);

  const disclosure = useMemo(
    () =>
      'We use essential storage to remember your privacy choice. With your permission, we may use analytics and advertising cookies, including Google AdSense, to improve the site and support free calculators.',
    [],
  );

  const savePreferences = (preferences: CookiePreferences) => {
    const payload = {
      ...preferences,
      essential: true as const,
      updatedAt: new Date().toISOString(),
      version: 1 as const,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    setStoredPreferences(payload);
    setDraftPreferences(payload);
    setVisible(false);
    setCustomizing(false);
    window.dispatchEvent(new CustomEvent('financialmetrics:cookie-consent', { detail: payload }));
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[80] px-4 pb-4 sm:px-6 sm:pb-6">
      <div className="mx-auto max-w-5xl rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl shadow-slate-900/15">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex gap-4">
            <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
              <Cookie className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-sm font-bold text-slate-900">Cookie and privacy choices</h2>
                {hasChoice && (
                  <button
                    type="button"
                    onClick={() => {
                      setVisible(false);
                      setCustomizing(false);
                    }}
                    className="rounded-full p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-900 lg:hidden"
                    aria-label="Close cookie settings"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">{disclosure}</p>
              <p className="mt-2 text-xs leading-5 text-slate-500">
                Read the <Link className="font-bold text-slate-900 underline decoration-slate-300 underline-offset-4" to="/cookie-policy">Cookie Policy</Link>.
              </p>
            </div>
          </div>

          {!customizing ? (
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row lg:flex-col">
              <button
                type="button"
                onClick={() => savePreferences({ ...defaultPreferences, analytics: true, advertising: true })}
                className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-black"
              >
                Accept all <ShieldCheck className="ml-2 h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => savePreferences(defaultPreferences)}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-900 hover:bg-slate-50"
              >
                Essential only
              </button>
              <button
                type="button"
                onClick={() => setCustomizing(true)}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-900 hover:bg-slate-100"
              >
                Customize <Settings className="ml-2 h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="w-full shrink-0 space-y-3 lg:w-80">
              {[
                ['analytics', 'Analytics cookies', 'Help us understand aggregate page usage and improve calculators.'],
                ['advertising', 'Advertising cookies', 'Allow Google AdSense and ad-related storage where legally permitted.'],
              ].map(([key, label, help]) => (
                <label key={key} className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <input
                    type="checkbox"
                    checked={draftPreferences[key as 'analytics' | 'advertising']}
                    onChange={(event) =>
                      setDraftPreferences((current) => ({
                        ...current,
                        [key]: event.target.checked,
                      }))
                    }
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                  />
                  <span>
                    <span className="block text-sm font-bold text-slate-900">{label}</span>
                    <span className="mt-1 block text-xs leading-5 text-slate-500">{help}</span>
                  </span>
                </label>
              ))}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => savePreferences(draftPreferences)}
                  className="flex-1 rounded-xl bg-slate-900 px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-black"
                >
                  Save choices
                </button>
                <button
                  type="button"
                  onClick={() => savePreferences(defaultPreferences)}
                  className="flex-1 rounded-xl border border-slate-200 bg-white px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-900 hover:bg-slate-50"
                >
                  Reject all
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
